import type { IArticleRepository } from "@/core/repositories/interfaces/IArticleRepository";
import type {
  Article,
  ArticleAdminListItem,
  ArticleRedactionListItem,
  ArticleEditor,
  ArticleSuggestionSearchEvent,
  ArticleSuggestionSearchPlace,
  ArticleSuggestedEvent,
  ArticleSuggestedPlace,
  CreateArticleInput,
  ArticleLibraryTab,
  ArticleLibraryTabCounts,
  ArticleListPage,
  ArticleSummary,
  ArticleLibrarySort,
  ArticleLibrarySortState,
  UpdateArticleContentInput,
  UpdateArticleInput,
  UpdateArticleAssignmentInput,
  UpdateArticleInternalNotesInput,
  UpdateArticleCoverImageInput,
  UpdateArticleStatusInput,
  ArticleTaxonomy,
  UpdateArticleTaxonomyInput,
} from "@/core/services/types/article.types";
import {
  ARTICLE_CREATE_DEFAULT_TITLE,
  ARTICLE_LIBRARY_TABS,
  type ContentStatus,
  editStatusToDbFields,
} from "@/core/services/types/article.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import { createClient } from "@/lib/supabase";

const ARTICLE_SUMMARY_COLUMNS =
  "id, title, sub_title, cover_image_url, published_at" as const;

const ARTICLE_DETAIL_COLUMNS =
  `${ARTICLE_SUMMARY_COLUMNS}, content_rich_text` as const;

const ARTICLE_ADMIN_LIST_SELECT =
  "id, title, published_at, created_at, status, assigned_to, cover_image_url, sub_title, content_rich_text, assignee:team_members!articles_assigned_to_fkey(id, first_name, last_name)" as const;

type ArticleAdminListRow = {
  id: string;
  title: string;
  published_at: string | null;
  created_at: string;
  status: ArticleAdminListItem["status"];
  assigned_to: string | null;
  cover_image_url: string | null;
  sub_title: string | null;
  content_rich_text: string | null;
  assignee: ArticleAdminListItem["assignee"] | ArticleAdminListItem["assignee"][];
};

function normalizeArticleAdminListRow(row: ArticleAdminListRow): ArticleAdminListItem {
  const assignee = Array.isArray(row.assignee) ? (row.assignee[0] ?? null) : row.assignee;

  return {
    id: row.id,
    title: row.title,
    published_at: row.published_at,
    created_at: row.created_at,
    status: row.status,
    assigned_to: row.assigned_to,
    cover_image_url: row.cover_image_url,
    sub_title: row.sub_title,
    content_rich_text: row.content_rich_text,
    assignee,
  };
}

const ARTICLE_REDACTION_LIST_SELECT =
  "id, title, sub_title, content_rich_text, published_at, status, assigned_to, cover_image_url, assignee:team_members!articles_assigned_to_fkey(id, first_name, last_name), place_suggestions:article_suggested_places(count), event_suggestions:article_suggested_events(count)" as const;

type ArticleRedactionListRow = ArticleAdminListRow & {
  sub_title: string | null;
  content_rich_text: string | null;
  cover_image_url: string | null;
  place_suggestions: { count: number }[] | { count: number } | null;
  event_suggestions: { count: number }[] | { count: number } | null;
};

function readRelationCount(
  value: { count: number }[] | { count: number } | null | undefined,
): number {
  if (!value) return 0;
  if (Array.isArray(value)) return value[0]?.count ?? 0;
  return value.count ?? 0;
}

function normalizeArticleRedactionListRow(
  row: ArticleRedactionListRow,
): ArticleRedactionListItem {
  const base = normalizeArticleAdminListRow(row);

  return {
    ...base,
    cover_image_url: row.cover_image_url,
    sub_title: row.sub_title,
    content_rich_text: row.content_rich_text,
    suggestedPlacesCount: readRelationCount(row.place_suggestions),
    suggestedEventsCount: readRelationCount(row.event_suggestions),
  };
}

const ARTICLE_EDITOR_COLUMNS =
  "id, title, sub_title, cover_image_url, content_rich_text, status, published_at, assigned_to, updated_at, internal_notes" as const;

function escapeIlikePattern(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/%/g, "\\%")
    .replace(/_/g, "\\_");
}

function buildTitleSubtitleSearchFilter(search: string): string {
  const pattern = `%${escapeIlikePattern(search)}%`;
  return `title.ilike."${pattern}",sub_title.ilike."${pattern}"`;
}

function buildPlaceSuggestionSearchFilter(search: string): string {
  const pattern = `%${escapeIlikePattern(search)}%`;
  return `name.ilike."${pattern}",city.ilike."${pattern}"`;
}

function buildEventSuggestionSearchFilter(search: string): string {
  const pattern = `%${escapeIlikePattern(search)}%`;
  return `title.ilike."${pattern}",description_short.ilike."${pattern}"`;
}

function normalizeSuggestedRelation<T>(value: T | T[] | null): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

type ArticleTaxonomyRow = {
  taxonomy_id: string;
  taxonomy: { category: string } | { category: string }[] | null;
};

type SuggestedPlaceRow = {
  position: number | null;
  place: {
    id: string;
    name: string;
    city: string | null;
    status: ContentStatus;
  } | null;
};

type SuggestedEventRow = {
  position: number | null;
  event: {
    id: string;
    title: string;
    start_date: string;
    end_date: string;
    status: ContentStatus;
    event_category: ArticleSuggestedEvent["eventCategory"];
  } | null;
};

function applySearchFilter<T extends { or: (filters: string) => T }>(
  query: T,
  search: string,
): T {
  if (!search) return query;
  return query.or(buildTitleSubtitleSearchFilter(search));
}

function emptyLibraryTabCounts(): ArticleLibraryTabCounts {
  return {
    ALL: 0,
    DRAFT: 0,
    PENDING_REVIEW: 0,
    SCHEDULED: 0,
    PUBLISHED: 0,
    ARCHIVED: 0,
  };
}

type OrderableQuery = {
  order: (
    column: string,
    options?: {
      ascending?: boolean;
      foreignTable?: string;
      nullsFirst?: boolean;
    },
  ) => OrderableQuery;
};

function applyArticleLibrarySortColumn(
  query: OrderableQuery,
  sort: ArticleLibrarySort,
): OrderableQuery {
  const ascending = sort.direction === "asc";

  switch (sort.column) {
    case "title":
      return query.order("title", { ascending });
    case "assignment":
      return query.order("last_name", {
        ascending,
        foreignTable: "team_members",
        nullsFirst: true,
      });
    case "created_at":
      return query.order("created_at", { ascending });
    case "published_at":
      return query.order("published_at", {
        ascending,
        nullsFirst: ascending,
      });
    case "status":
      return query.order("status", { ascending });
  }
}

function applyArticleLibraryOrder(
  query: OrderableQuery,
  tab: ArticleLibraryTab,
  sort: ArticleLibrarySortState,
): OrderableQuery {
  if (sort) {
    return applyArticleLibrarySortColumn(query, sort);
  }

  if (tab === "ALL") {
    return query.order("updated_at", { ascending: false });
  }

  if (tab === "PUBLISHED") {
    return query.order("published_at", { ascending: false });
  }

  return query.order("updated_at", { ascending: false });
}

function getCurrentMonthUtcRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

export class ArticleRepository implements IArticleRepository {
  async createAdmin(input: CreateArticleInput): Promise<ArticleEditor | null> {
    const supabase = await createClient();
    const title = input.title?.trim() || ARTICLE_CREATE_DEFAULT_TITLE;

    const { data, error } = await supabase
      .from("articles")
      .insert({
        title,
        status: input.status ?? "DRAFT",
        ...(input.assignedTo ? { assigned_to: input.assignedTo } : {}),
        source_type: "MANUAL",
        publish_on_web: false,
        publish_on_mobile: false,
      })
      .select(ARTICLE_EDITOR_COLUMNS)
      .single();

    if (error) {
      console.error("[ArticleRepository.createAdmin]", {
        message: error.message,
        code: error.code,
        details: error.details,
      });
      return null;
    }

    return data;
  }

  async deleteAdmin(id: string): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.from("articles").delete().eq("id", id);

    if (error) {
      console.error("[ArticleRepository.deleteAdmin]", error.message);
      return false;
    }

    return true;
  }

  async findPublished(limit = 10): Promise<ArticleSummary[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .select(ARTICLE_SUMMARY_COLUMNS)
      .eq("status", "PUBLISHED")
      .eq("publish_on_web", true)
      .order("published_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[ArticleRepository.findPublished]", error.message);
      return [];
    }

    return data ?? [];
  }

  async findById(id: string): Promise<Article | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .select(ARTICLE_DETAIL_COLUMNS)
      .eq("id", id)
      .eq("status", "PUBLISHED")
      .eq("publish_on_web", true)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.findById]", error.message);
      return null;
    }

    return data;
  }

  async findAdminById(id: string): Promise<ArticleEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .select(ARTICLE_EDITOR_COLUMNS)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.findAdminById]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminContent(
    id: string,
    input: UpdateArticleContentInput,
  ): Promise<ArticleEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .update({
        title: input.title,
        sub_title: input.sub_title,
        content_rich_text: input.content_rich_text,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(ARTICLE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.updateAdminContent]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminStatus(
    id: string,
    input: UpdateArticleStatusInput,
  ): Promise<ArticleEditor | null> {
    const supabase = await createClient();
    const existing = await this.findAdminById(id);

    if (!existing) {
      return null;
    }

    const statusFields = editStatusToDbFields(
      input.editStatus,
      existing.published_at,
      {
        schedulePublishAt: input.schedulePublishAt,
        publishImmediately: input.publishImmediately,
      },
    );

    const { data, error } = await supabase
      .from("articles")
      .update({
        status: statusFields.status,
        published_at: statusFields.published_at,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(ARTICLE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.updateAdminStatus]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminAssignment(
    id: string,
    input: UpdateArticleAssignmentInput,
  ): Promise<ArticleEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .update({
        assigned_to: input.assignedTo,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(ARTICLE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.updateAdminAssignment]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminInternalNotes(
    id: string,
    input: UpdateArticleInternalNotesInput,
  ): Promise<ArticleEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .update({
        internal_notes: input.internalNotes,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(ARTICLE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.updateAdminInternalNotes]", error.message);
      return null;
    }

    return data;
  }

  async updateAdminCoverImage(
    id: string,
    input: UpdateArticleCoverImageInput,
  ): Promise<ArticleEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .update({
        cover_image_url: input.coverImageUrl,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(ARTICLE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.updateAdminCoverImage]", error.message);
      return null;
    }

    return data;
  }

  async updateAdmin(
    id: string,
    input: UpdateArticleInput,
  ): Promise<ArticleEditor | null> {
    const supabase = await createClient();
    const existing = await this.findAdminById(id);

    if (!existing) {
      return null;
    }

    const statusFields = editStatusToDbFields(
      input.editStatus,
      existing.published_at,
    );

    const { data, error } = await supabase
      .from("articles")
      .update({
        title: input.title,
        sub_title: input.sub_title,
        content_rich_text: input.content_rich_text,
        status: statusFields.status,
        published_at: statusFields.published_at,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(ARTICLE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[ArticleRepository.updateAdmin]", error.message);
      return null;
    }

    return data;
  }

  async findByLibraryTab(
    tab: ArticleLibraryTab,
    page: number,
    pageSize: LibraryPageSize,
    search = "",
    sort: ArticleLibrarySortState = null,
  ): Promise<ArticleListPage> {
    const supabase = await createClient();
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("articles")
      .select(ARTICLE_ADMIN_LIST_SELECT, { count: "exact" });

    query = applySearchFilter(query, search);

    if (tab === "PUBLISHED") {
      query = query.eq("status", "PUBLISHED");
    } else if (tab !== "ALL") {
      query = query.eq("status", tab);
    }

    query = applyArticleLibraryOrder(query, tab, sort) as typeof query;

    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.error("[ArticleRepository.findByLibraryTab]", error.message);
      return { items: [], total: 0, page, pageSize };
    }

    return {
      items: (data ?? []).map((row) =>
        normalizeArticleAdminListRow(row as ArticleAdminListRow),
      ),
      total: count ?? 0,
      page,
      pageSize,
    };
  }

  async findAssignedForRedaction(
    assigneeId: string,
    status: ContentStatus,
  ): Promise<ArticleRedactionListItem[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("articles")
      .select(ARTICLE_REDACTION_LIST_SELECT)
      .eq("assigned_to", assigneeId)
      .eq("status", status)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("[ArticleRepository.findAssignedForRedaction]", error.message);
      return [];
    }

    return (data ?? []).map((row) =>
      normalizeArticleRedactionListRow(row as ArticleRedactionListRow),
    );
  }

  async countByLibraryTab(): Promise<ArticleLibraryTabCounts> {
    const supabase = await createClient();
    const counts = emptyLibraryTabCounts();

    await Promise.all([
      (async () => {
        const { count, error } = await supabase
          .from("articles")
          .select("id", { count: "exact", head: true });

        if (error) {
          console.error(
            "[ArticleRepository.countByLibraryTab] ALL",
            error.message,
          );
          return;
        }

        counts.ALL = count ?? 0;
      })(),
      ...ARTICLE_LIBRARY_TABS.filter((tab) => tab !== "ALL").map(async (status) => {
        const { count, error } = await supabase
          .from("articles")
          .select("id", { count: "exact", head: true })
          .eq("status", status);

        if (error) {
          console.error(
            `[ArticleRepository.countByLibraryTab] ${status}`,
            error.message,
          );
          return;
        }

        counts[status] = count ?? 0;
      }),
    ]);

    return counts;
  }

  async countPublishedThisMonth(): Promise<number> {
    const supabase = await createClient();
    const { start, end } = getCurrentMonthUtcRange();
    const now = new Date().toISOString();

    const { count, error } = await supabase
      .from("articles")
      .select("id", { count: "exact", head: true })
      .eq("status", "PUBLISHED")
      .gte("published_at", start)
      .lt("published_at", end)
      .lte("published_at", now);

    if (error) {
      console.error(
        "[ArticleRepository.countPublishedThisMonth]",
        error.message,
      );
      return 0;
    }

    return count ?? 0;
  }

  async findSuggestedPlacesByArticleId(
    articleId: string,
  ): Promise<ArticleSuggestedPlace[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("article_suggested_places")
      .select("position, place:places(id, name, city, status)")
      .eq("article_id", articleId)
      .order("position", { ascending: true });

    if (error) {
      console.error(
        "[ArticleRepository.findSuggestedPlacesByArticleId]",
        error.message,
      );
      return [];
    }

    const results: ArticleSuggestedPlace[] = [];

    for (const row of (data ?? []) as SuggestedPlaceRow[]) {
      const place = normalizeSuggestedRelation(row.place);
      if (!place) continue;

      results.push({
        placeId: place.id,
        position: row.position,
        name: place.name,
        city: place.city,
        status: place.status,
      });
    }

    return results;
  }

  async findSuggestedEventsByArticleId(
    articleId: string,
  ): Promise<ArticleSuggestedEvent[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("article_suggested_events")
      .select(
        "position, event:events(id, title, start_date, end_date, status, event_category)",
      )
      .eq("article_id", articleId)
      .order("position", { ascending: true });

    if (error) {
      console.error(
        "[ArticleRepository.findSuggestedEventsByArticleId]",
        error.message,
      );
      return [];
    }

    const results: ArticleSuggestedEvent[] = [];

    for (const row of (data ?? []) as SuggestedEventRow[]) {
      const event = normalizeSuggestedRelation(row.event);
      if (!event) continue;

      results.push({
        eventId: event.id,
        position: row.position,
        title: event.title,
        startDate: event.start_date,
        endDate: event.end_date,
        status: event.status,
        eventCategory: event.event_category,
      });
    }

    return results;
  }

  async addSuggestedPlace(
    articleId: string,
    placeId: string,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { data: existing } = await supabase
      .from("article_suggested_places")
      .select("place_id")
      .eq("article_id", articleId)
      .eq("place_id", placeId)
      .maybeSingle();

    if (existing) return true;

    const { data: lastRow } = await supabase
      .from("article_suggested_places")
      .select("position")
      .eq("article_id", articleId)
      .order("position", { ascending: false })
      .limit(1)
      .maybeSingle();

    const position = (lastRow?.position ?? -1) + 1;

    const { error } = await supabase.from("article_suggested_places").insert({
      article_id: articleId,
      place_id: placeId,
      position,
    });

    if (error) {
      console.error("[ArticleRepository.addSuggestedPlace]", error.message);
      return false;
    }

    return true;
  }

  async removeSuggestedPlace(
    articleId: string,
    placeId: string,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase
      .from("article_suggested_places")
      .delete()
      .eq("article_id", articleId)
      .eq("place_id", placeId);

    if (error) {
      console.error("[ArticleRepository.removeSuggestedPlace]", error.message);
      return false;
    }

    return true;
  }

  async addSuggestedEvent(
    articleId: string,
    eventId: string,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { data: existing } = await supabase
      .from("article_suggested_events")
      .select("event_id")
      .eq("article_id", articleId)
      .eq("event_id", eventId)
      .maybeSingle();

    if (existing) return true;

    const { data: lastRow } = await supabase
      .from("article_suggested_events")
      .select("position")
      .eq("article_id", articleId)
      .order("position", { ascending: false })
      .limit(1)
      .maybeSingle();

    const position = (lastRow?.position ?? -1) + 1;

    const { error } = await supabase.from("article_suggested_events").insert({
      article_id: articleId,
      event_id: eventId,
      position,
    });

    if (error) {
      console.error("[ArticleRepository.addSuggestedEvent]", error.message);
      return false;
    }

    return true;
  }

  async removeSuggestedEvent(
    articleId: string,
    eventId: string,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase
      .from("article_suggested_events")
      .delete()
      .eq("article_id", articleId)
      .eq("event_id", eventId);

    if (error) {
      console.error("[ArticleRepository.removeSuggestedEvent]", error.message);
      return false;
    }

    return true;
  }

  async searchPlacesForSuggestion(
    query: string,
    limit: number,
  ): Promise<ArticleSuggestionSearchPlace[]> {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .select("id, name, city, status")
      .or(buildPlaceSuggestionSearchFilter(trimmed))
      .order("name", { ascending: true })
      .limit(limit);

    if (error) {
      console.error(
        "[ArticleRepository.searchPlacesForSuggestion]",
        error.message,
      );
      return [];
    }

    return data ?? [];
  }

  async searchEventsForSuggestion(
    query: string,
    limit: number,
  ): Promise<ArticleSuggestionSearchEvent[]> {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    const supabase = await createClient();

    const { data, error } = await supabase
      .from("events")
      .select("id, title, start_date, end_date, status, event_category")
      .or(buildEventSuggestionSearchFilter(trimmed))
      .order("start_date", { ascending: false })
      .limit(limit);

    if (error) {
      console.error(
        "[ArticleRepository.searchEventsForSuggestion]",
        error.message,
      );
      return [];
    }

    return data ?? [];
  }

  async findTaxonomy(articleId: string): Promise<ArticleTaxonomy> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("article_taxonomy")
      .select("taxonomy_id, taxonomy:taxonomy_id(category)")
      .eq("article_id", articleId);

    if (error) {
      console.error("[ArticleRepository.findTaxonomy]", error.message);
      return {
        artFormIds: [],
        artisticPeriodIds: [],
        culturalAreaIds: [],
        exhibitionTypeIds: [],
      };
    }

    const artFormIds: string[] = [];
    const artisticPeriodIds: string[] = [];
    const culturalAreaIds: string[] = [];
    const exhibitionTypeIds: string[] = [];

    for (const row of (data ?? []) as ArticleTaxonomyRow[]) {
      const taxonomy = normalizeSuggestedRelation(row.taxonomy);
      if (!taxonomy) continue;

      if (taxonomy.category === "ART_FORM") {
        artFormIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "ARTISTIC_PERIOD") {
        artisticPeriodIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "CULTURAL_AREA") {
        culturalAreaIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "EXHIBITION_TYPE") {
        exhibitionTypeIds.push(row.taxonomy_id);
      }
    }

    return { artFormIds, artisticPeriodIds, culturalAreaIds, exhibitionTypeIds };
  }

  async replaceTaxonomy(
    articleId: string,
    input: UpdateArticleTaxonomyInput,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.rpc("replace_content_taxonomy", {
      p_entity_type: "article",
      p_entity_id: articleId,
      p_taxonomy_ids: [
        ...input.artFormIds,
        ...input.artisticPeriodIds,
        ...input.culturalAreaIds,
        ...input.exhibitionTypeIds,
      ],
    });

    if (error) {
      console.error("[ArticleRepository.replaceTaxonomy]", error.message);
      return false;
    }
    return true;
  }
}
